import { Injectable, inject } from '@angular/core';
import { MembershipV2Service } from 'app/core/membership/membership-v2.service';
import {
    BehaviorSubject,
    Observable,
    Subscription,
    interval,
    switchMap,
    take,
    takeWhile,
} from 'rxjs';
import { UserService } from '../user/user.service';
import { TransactionService } from '../transaction/transaction.service';

export type ExportJobKind = 'member-list' | 'incentive-statement' | 'dealer-subscribers' | 'dealer-corporates' | 'transaction-statement';

export type ExportJobStatusValue =
    | 'PENDING'
    | 'PROCESSING'
    | 'READY'
    | 'FAILED';

export interface PersistentExportJob {
    jobId: string;
    status: ExportJobStatusValue;
    createdAt: string;
    completedAt?: string;
    errorMessage?: string;
    downloadUrl?: string;
    kind: ExportJobKind;
    label: string;
    filenamePrefix: string;
    _pollTimedOut?: boolean;
}

export interface MemberListExportParams {
    memberId: string;
}

export interface IncentiveStatementExportParams {
    memberId: string;
    periodYear: number;
}

@Injectable({ providedIn: 'root' })
export class ExportJobsService {
    private readonly _storageKey = 'eastel.subscriber.exportJobs';
    private readonly _maxPollAttempts = 60;
    private readonly _jobs = new BehaviorSubject<PersistentExportJob[]>(
        this._loadJobs()
    );
    private readonly _panelOpen = new BehaviorSubject<boolean>(false);
    private readonly _pollSubscriptions = new Map<string, Subscription>();
    private readonly _membershipV2Service = inject(MembershipV2Service);
    private readonly _userService = inject(UserService);
    private readonly _transactionService = inject(TransactionService);

    readonly jobs$ = this._jobs.asObservable();
    readonly panelOpen$ = this._panelOpen.asObservable();

    constructor() {
        localStorage.removeItem(this._storageKey);

        this._jobs.value
            .filter((job) => this._isActive(job))
            .forEach((job) => this.startPolling(job));

        this._userService.user$.subscribe((user) => {
            if (!user) {
                this.clear();
            }
        });
    }

    request(
        kind: ExportJobKind,
        params: any = {}
    ): Observable<PersistentExportJob> {
        return new Observable((subscriber) => {
            const subscription = this._requestByKind(kind, params).subscribe({
                next: (response) => {
                    const job = this._decorateJob(kind, response);
                    this._upsertJob(job);
                    this.startPolling(job);
                    subscriber.next(job);
                    subscriber.complete();
                },
                error: (error) => subscriber.error(error),
            });

            return () => subscription.unsubscribe();
        });
    }

    startPolling(job: PersistentExportJob): void {
        this._pollSubscriptions.get(job.jobId)?.unsubscribe();

        const subscription = interval(3000)
            .pipe(
                take(this._maxPollAttempts),
                switchMap(() => this._statusByKind(job.kind, job.jobId)),
                takeWhile(
                    (response) =>
                        response.status !== 'READY' &&
                        response.status !== 'FAILED',
                    true
                )
            )
            .subscribe({
                next: (response) => {
                    this._upsertJob({
                        ...this._decorateJob(job.kind, response),
                        label: job.label,
                        filenamePrefix: job.filenamePrefix,
                    });
                },
                complete: () => {
                    this._pollSubscriptions.delete(job.jobId);
                    const current = this._jobs.value.find(
                        (item) => item.jobId === job.jobId
                    );
                    if (current && this._isActive(current)) {
                        this._upsertJob({ ...current, _pollTimedOut: true });
                    }
                },
                error: () => {
                    this._pollSubscriptions.delete(job.jobId);
                },
            });

        this._pollSubscriptions.set(job.jobId, subscription);
    }

    restart(job: PersistentExportJob): void {
        const next = { ...job, _pollTimedOut: false };
        this._upsertJob(next);
        this.startPolling(next);
    }

    download(job: PersistentExportJob): Observable<Blob> {
        switch (job.kind) {
            case 'member-list':
                return this._membershipV2Service.downloadMemberListExport(
                    job.jobId
                );
            case 'incentive-statement':
                return this._membershipV2Service.downloadIncentiveStatementExport(
                    job.jobId
                );
            case 'dealer-subscribers':
                return this._membershipV2Service.downloadDealerSubscribersExport(
                    job.jobId
                );
            case 'dealer-corporates':
                return this._membershipV2Service.downloadDealerCorporatesExport(
                    job.jobId
                );
            case 'transaction-statement':
                return this._transactionService.downloadTransactionExport(
                    job.jobId
                );
        }
    }

    clear(): void {
        this._pollSubscriptions.forEach((subscription) =>
            subscription.unsubscribe()
        );
        this._pollSubscriptions.clear();
        this._setJobs([]);
        this.closePanel();
    }

    remove(job: PersistentExportJob): void {
        this._pollSubscriptions.get(job.jobId)?.unsubscribe();
        this._pollSubscriptions.delete(job.jobId);

        const jobs = this._jobs.value.filter(
            (item) => !(item.kind === job.kind && item.jobId === job.jobId)
        );
        this._setJobs(jobs);

        if (jobs.length === 0) {
            this.closePanel();
        }
    }

    openPanel(): void {
        this._panelOpen.next(true);
    }

    closePanel(): void {
        this._panelOpen.next(false);
    }

    togglePanel(): void {
        this._panelOpen.next(!this._panelOpen.value);
    }

    private _requestByKind(kind: ExportJobKind, params: any): Observable<any> {
        switch (kind) {
            case 'member-list':
                return this._membershipV2Service.requestMemberListExport(
                    (params as MemberListExportParams).memberId
                );
            case 'incentive-statement':
                return this._membershipV2Service.requestIncentiveStatementExport(
                    (params as IncentiveStatementExportParams).memberId,
                    (params as IncentiveStatementExportParams).periodYear
                );
            case 'dealer-subscribers':
                return this._membershipV2Service.requestDealerSubscribersExport(
                    params.userId,
                    params.globalSearch,
                    params.registrationType,
                    params.status
                );
            case 'dealer-corporates':
                return this._membershipV2Service.requestDealerCorporatesExport(
                    params.userId,
                    params.dealerProfileId,
                    params.category
                );
            case 'transaction-statement':
                return this._transactionService.requestTransactionExport(params);
        }
    }

    private _statusByKind(kind: ExportJobKind, jobId: string): Observable<any> {
        switch (kind) {
            case 'member-list':
                return this._membershipV2Service.getMemberListExportJobStatus(
                    jobId
                );
            case 'incentive-statement':
                return this._membershipV2Service.getIncentiveStatementExportJobStatus(
                    jobId
                );
            case 'dealer-subscribers':
                return this._membershipV2Service.getDealerSubscribersExportJobStatus(
                    jobId
                );
            case 'dealer-corporates':
                return this._membershipV2Service.getDealerCorporatesExportJobStatus(
                    jobId
                );
            case 'transaction-statement':
                return this._transactionService.getTransactionExportJobStatus(jobId);
        }
    }

    private _decorateJob(
        kind: ExportJobKind,
        response: any
    ): PersistentExportJob {
        return {
            jobId: response.jobId,
            status: response.status,
            createdAt: response.createdAt,
            completedAt: response.completedAt,
            errorMessage: response.errorMessage,
            downloadUrl: response.downloadUrl,
            kind,
            label: this._getLabel(kind),
            filenamePrefix: this._getFilenamePrefix(kind),
        };
    }

    private _getLabel(kind: ExportJobKind): string {
        switch (kind) {
            case 'member-list':
                return 'Member List';
            case 'incentive-statement':
                return 'Incentive Statement';
            case 'dealer-subscribers':
                return 'Subscriber List';
            case 'dealer-corporates':
                return 'Corporates List';
            case 'transaction-statement':
                return 'Transaction Statement';
        }
    }

    private _getFilenamePrefix(kind: ExportJobKind): string {
        switch (kind) {
            case 'member-list':
                return 'member_list';
            case 'incentive-statement':
                return 'incentive_statement';
            case 'dealer-subscribers':
                return 'subscriber_list';
            case 'dealer-corporates':
                return 'corporates_list';
            case 'transaction-statement':
                return 'transaction_statement';
        }
    }

    private _upsertJob(job: PersistentExportJob): void {
        const jobs = this._jobs.value.filter(
            (item) => item.jobId !== job.jobId
        );
        this._setJobs([job, ...jobs]);
    }

    private _setJobs(jobs: PersistentExportJob[]): void {
        this._jobs.next(jobs);
        this._saveJobs(jobs);
    }

    private _isActive(job: PersistentExportJob): boolean {
        return job.status === 'PENDING' || job.status === 'PROCESSING';
    }

    private _loadJobs(): PersistentExportJob[] {
        try {
            const raw = sessionStorage.getItem(this._storageKey);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    }

    private _saveJobs(jobs: PersistentExportJob[]): void {
        try {
            if (jobs.length === 0) {
                sessionStorage.removeItem(this._storageKey);
                return;
            }

            sessionStorage.setItem(this._storageKey, JSON.stringify(jobs));
        } catch {
            // Keep in-memory state even when browser storage is unavailable.
        }
    }
}
